import {computed, inject, Injectable, signal} from '@angular/core'
import {HttpClient, HttpHeaders} from '@angular/common/http'
import {IExchangeResponse, IGitLabAuth, ISelf} from '../1-application/types'
import {Observable, of, switchMap, tap, throwError} from 'rxjs'
import {environment} from '../../environments/environment'
import {LOCAL_STORAGE} from '../0-general/providers'
import {ACCESS_TOKEN_NAME, GITLAB_TOKEN_NAME} from '../1-application/constants'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * The app access token, if present we are logged in.
   */
  /* v8 ignore next @preserve */
  public accessToken = signal<Partial<IExchangeResponse>>({})

  /**
   * Gitlab logged in access token.
   */
  /* v8 ignore next @preserve */
  public gitLabToken = signal<Partial<IGitLabAuth>>({})

  /**
   * Rudimentary for now, will expand to scopes etc.
   */
  /* v8 ignore next @preserve */
  public loggedIn = computed(() => {
    return this.accessToken().accessToken !== undefined
  })

  private readonly http = inject(HttpClient)

  private readonly storage = inject(LOCAL_STORAGE)

  /**
   * We need to replace this with a "bootstrap" function in the
   * application.
   */
  constructor() {
    this.boostrap().subscribe()
  }

  public boostrap(): Observable<any> {
    const accessToken = this.storage.getItem(ACCESS_TOKEN_NAME)
    if (accessToken) {
      this.accessToken.set(JSON.parse(accessToken))
    }

    const gitlabToken = this.storage.getItem(GITLAB_TOKEN_NAME)
    if (gitlabToken) {
      this.gitLabToken.set(JSON.parse(gitlabToken))
    }
    return of({})
  }

  /**
   * Returns the access token in a header ready to be used.
   *
   * Makes a refresh if needed.
   */
  public getAccessToken(): Observable<HttpHeaders> {

    const token = this.accessToken()
    if (!token?.expires) {
      /* v8 ignore next */
      return throwError(
        /* v8 ignore next */
        () => {
          /* This is actually tested but no coverage is recorded. */
          return new Error('Token expired')
        })
    }
    if ((Date.now()) + 10_000 > token.expires) {
      return this.refresh().pipe(
        switchMap(() => {
          return this.createHeaders()
        })
      )
    }
    return this.createHeaders()
  }

  /**
   * Updates both local storage and the signal. The actual
   * token is created by the gitlab-token service.
   *
   * @param token - The complete token info.
   */
  public updateGitlabToken(token: IGitLabAuth): Observable<IGitLabAuth> {
    this.storage.setItem(GITLAB_TOKEN_NAME, JSON.stringify(token))
    this.gitLabToken.set(token)
    return of(token)
  }

  /**
   * This is a complete "exchange" after first identification that
   * sets both the local "auth" and the GitLab token.
   * @param state
   * @param nonce
   */
  public exchange(state: string, nonce: string): Observable<any> {
    return this.http.put<IExchangeResponse>(
      `${environment.apiUrl}/exchange`, {state, nonce})
      .pipe(
        tap(res => {
          this.updateGitlabToken(res.gitlab!)
          delete res.gitlab
          res.expires = res.expires * 1000 + Date.now()
          this.storage.setItem(ACCESS_TOKEN_NAME, JSON.stringify(res))
          this.accessToken.set(res)
        }),
        switchMap(() => this.getSelf())
      )
  }

  public getSelf(): Observable<any> {
    return this.getAccessToken()
      .pipe(switchMap(
        headers => {
          return this.http.get<ISelf>(`${environment.apiUrl}/user/self`, {headers})
        }
      ))
  }

  /**
   * Request a refresh of our token. Only public for testing.
   */
  private refresh(): Observable<IExchangeResponse> {
    const rt = this.accessToken().refreshToken
    return this.http.get<IExchangeResponse>(`${environment.apiUrl}/refresh?rt=${rt}`)
      .pipe(
        tap(res => {
          res.expires = Date.now() + (res.expires * 1000)
          this.accessToken.set(res)
          this.storage.setItem(ACCESS_TOKEN_NAME, JSON.stringify(res))
        })
      )
  }

  private createHeaders(): Observable<HttpHeaders> {
    return of(new HttpHeaders()
      .set('Authorization', `Bearer ${this.accessToken().accessToken}`))
  }
}

